import React, { useLayoutEffect, useRef, useState } from "react";
import { CheckCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { evaluateMeasuredValue, isMeasurableCriterion } from "@/lib/aql";
import { DEFECT_SEVERITY_LABELS } from "../StageDefectSelectorTable";
import { QC_RESULT_LABELS, type IsoCriterionResult } from "./checklist";

type Result = IsoCriterionResult["result"];

const SOURCE_LABELS: Record<string, string> = {
  SUPPLIER_COA: "Theo CoA",
  EXTERNAL_LAB: "Gửi lab",
  SHOPFLOOR_MEASURED: "Đo tại xưởng",
};

const GENERIC_CATEGORIES = new Set(["MEASUREMENT", "VISUAL", "", "UNDEFINED", "NULL"]);

export function isQuantitativeCriterion(item: Pick<IsoCriterionResult, "type">) {
  return item?.type === "QUANTITATIVE" || item?.type === "EVALUATION";
}

function num(v: unknown): number | null {
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Tiêu chuẩn hiển thị: định lượng `7.0 % (6.0–8.5)`, `≤ 30 g/m²`; định tính: mô tả ngắn */
export function formatCriterionStandard(item: Partial<IsoCriterionResult>): string {
  if (isQuantitativeCriterion(item as IsoCriterionResult)) {
    const min = num(item.minValue);
    const max = num(item.maxValue);
    const target = num(item.targetValue);
    const unit = item.unit ? ` ${item.unit}` : "";
    let range = "";
    if (min !== null && max !== null) range = min < 0 || max < 0 ? `${min} – ${max}` : `${min}–${max}`;
    else if (min !== null) range = `≥ ${min}`;
    else if (max !== null) range = `≤ ${max}`;
    if (target !== null && range) return `${target}${unit} (${range})`;
    if (target !== null) return `${target}${unit}`;
    if (range) return `${range}${unit}`;
  }
  return String(item.standard || "").trim() || "—";
}

export function displayCriterionName(item: Partial<IsoCriterionResult> & { label?: string; criterion?: string }, idx = 0) {
  return item.shortName || item.name || item.label || item.criterion || `Chỉ tiêu #${idx + 1}`;
}

export function criterionSubline(item: Partial<IsoCriterionResult>): string {
  const parts: string[] = [];
  if (item.defectType) parts.push(DEFECT_SEVERITY_LABELS[item.defectType] || item.defectType);
  const tool = item.toolUsed || item.method;
  if (tool) parts.push(tool);
  if (item.verificationSource && item.verificationSource !== "SHOPFLOOR_MEASURED") {
    parts.push(SOURCE_LABELS[item.verificationSource] || item.verificationSource);
  }
  return parts.join(" · ");
}

function useContainerWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState<number>(1200);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

function ResultToggle({
  value,
  onChange,
  disabled,
  options = ["PASS", "FAIL", "NA"],
  size = "sm",
}: {
  value: Result;
  onChange: (r: Result) => void;
  disabled?: boolean;
  options?: Result[];
  size?: "sm" | "lg";
}) {
  return (
    <div className="inline-flex whitespace-nowrap rounded-md border border-slate-200 bg-slate-50 p-0.5" role="group">
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onChange(opt)}
            className={cn(
              "rounded font-bold transition-colors disabled:cursor-default",
              size === "lg" ? "px-3.5 py-2 text-xs" : "px-2.5 py-1 text-[11px]",
              active && opt === "PASS" && "bg-emerald-600 text-white shadow-2xs",
              active && opt === "FAIL" && "bg-red-600 text-white shadow-2xs",
              active && opt === "NA" && "bg-slate-600 text-white",
              !active && "text-slate-600 hover:bg-slate-200/60",
            )}
          >
            {QC_RESULT_LABELS[opt]}
          </button>
        );
      })}
    </div>
  );
}

export function ResultBadge({ result }: { result?: string | null }) {
  const r = String(result || "PENDING").toUpperCase();
  return (
    <Badge
      variant="outline"
      className={cn(
        "text-[11px] font-bold",
        r === "PASS" && "border-emerald-300 bg-emerald-50 text-emerald-700",
        r === "FAIL" && "border-red-300 bg-red-50 text-red-700",
        r === "NA" && "border-slate-200 bg-slate-100 text-slate-600",
        (r === "PENDING" || !QC_RESULT_LABELS[r]) && "border-slate-200 bg-white text-slate-400",
      )}
    >
      {QC_RESULT_LABELS[r] || "Chưa kiểm"}
    </Badge>
  );
}

function measuredText(item: IsoCriterionResult) {
  const v = item.measuredValue;
  if (v === undefined || v === null || v === "") return "—";
  return `${v}${item.unit ? ` ${item.unit}` : ""}`;
}

interface CriteriaTableProps {
  items: IsoCriterionResult[];
  onChange?: (items: IsoCriterionResult[]) => void;
  readOnly?: boolean;
  /** Ô chọn mẫu kiểm tra hoặc nút khác đặt ở đầu bảng */
  toolbar?: React.ReactNode;
  title?: string;
  emptyText?: string;
}

/**
 * Bảng chỉ tiêu thống nhất: Chỉ tiêu · Loại · Tiêu chuẩn · Kết quả · Đánh giá · Ghi chú.
 * Định tính: chọn Đạt / Không đạt / N/A. Định lượng: nhập số đo, đánh giá tự tính theo min–max.
 * Khung hẹp (< 720px): mỗi chỉ tiêu một thẻ.
 */
export function CriteriaTable({ items, onChange, readOnly, toolbar, title = "Chỉ tiêu kiểm tra", emptyText }: CriteriaTableProps) {
  const editable = Boolean(onChange) && !readOnly;
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const compact = width < 720;

  const update = (id: string, patch: (item: IsoCriterionResult) => Partial<IsoCriterionResult>) => {
    if (!onChange) return;
    onChange(items.map((it) => (it.id === id ? { ...it, ...patch(it) } : it)));
  };

  const setResult = (id: string, result: Result) => update(id, () => ({ result }));
  const setNote = (id: string, note: string) => update(id, () => ({ note }));
  const setMeasured = (id: string, raw: string) =>
    update(id, (it) => {
      const evaluated = evaluateMeasuredValue(raw, it.minValue, it.maxValue);
      if (raw === "") return { measuredValue: "", result: isMeasurableCriterion(it) ? "PENDING" : it.result };
      return { measuredValue: raw, result: evaluated ?? it.result };
    });

  const markAllPass = () => {
    if (!onChange) return;
    onChange(
      items.map((it) => (!isMeasurableCriterion(it) && (it.result === "PENDING" || !it.result) ? { ...it, result: "PASS" } : it)),
    );
  };

  const counts = {
    pass: items.filter((i) => i.result === "PASS").length,
    fail: items.filter((i) => i.result === "FAIL").length,
    pending: items.filter((i) => !i.result || i.result === "PENDING").length,
  };

  // Nhóm dòng theo nhóm chỉ tiêu của mẫu (bỏ qua nhóm mặc định đo lường / ngoại quan vì đã có cột Loại)
  const groups: Array<{ key: string; rows: Array<{ item: IsoCriterionResult; idx: number }> }> = [];
  const meaningful = items.some((i) => !GENERIC_CATEGORIES.has(String(i.category || "").toUpperCase()));
  items.forEach((item, idx) => {
    const key = meaningful ? String(item.category || "Khác") : "";
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, rows: [] };
      groups.push(g);
    }
    g.rows.push({ item, idx });
  });

  const renderResultCell = (item: IsoCriterionResult, large = false) => {
    if (isQuantitativeCriterion(item)) {
      if (!editable) return <span className="font-mono font-bold text-slate-900">{measuredText(item)}</span>;
      return (
        <div className="flex items-center gap-1">
          <Input
            type="number"
            step="any"
            inputMode="decimal"
            aria-label={`Kết quả ${displayCriterionName(item)}`}
            value={item.measuredValue ?? ""}
            disabled={item.result === "NA"}
            onChange={(e) => setMeasured(item.id, e.target.value)}
            placeholder={item.targetValue !== undefined && item.targetValue !== null ? String(item.targetValue) : "Số đo"}
            className={cn(
              "text-center font-mono font-bold",
              large ? "h-10 text-sm" : "h-8 text-xs",
              item.result === "FAIL" && "border-red-400 bg-red-50 text-red-800",
            )}
          />
          {item.unit ? <span className="shrink-0 text-[11px] text-slate-500">{item.unit}</span> : null}
        </div>
      );
    }
    return null;
  };

  const renderEvalCell = (item: IsoCriterionResult, large = false) => {
    const measurable = isMeasurableCriterion(item);
    if (isQuantitativeCriterion(item) && measurable) {
      return (
        <div className="flex items-center justify-center gap-1.5">
          <ResultBadge result={item.result} />
          {editable && (
            <button
              type="button"
              aria-pressed={item.result === "NA"}
              onClick={() =>
                setResult(
                  item.id,
                  item.result === "NA"
                    ? evaluateMeasuredValue(item.measuredValue, item.minValue, item.maxValue) ?? "PENDING"
                    : "NA",
                )
              }
              className={cn(
                "rounded border px-1.5 py-0.5 text-[10px] font-semibold",
                item.result === "NA" ? "border-slate-600 bg-slate-600 text-white" : "border-slate-200 text-slate-500 hover:bg-slate-100",
              )}
            >
              N/A
            </button>
          )}
        </div>
      );
    }
    if (!editable) return <ResultBadge result={item.result} />;
    return <ResultToggle value={item.result} onChange={(r) => setResult(item.id, r)} size={large ? "lg" : "sm"} />;
  };

  const noteInput = (item: IsoCriterionResult, large = false) =>
    editable ? (
      <Input
        aria-label={`Ghi chú ${displayCriterionName(item)}`}
        value={item.note || ""}
        onChange={(e) => setNote(item.id, e.target.value)}
        placeholder={item.result === "FAIL" ? "Vị trí / nguyên nhân lỗi" : "Ghi chú"}
        className={cn(large ? "h-9 text-sm" : "h-8 text-xs", item.result === "FAIL" && "border-red-300")}
      />
    ) : (
      <span className="text-slate-600">{item.note || "—"}</span>
    );

  return (
    <div ref={ref} className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xs" data-testid="qc-criteria-table">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3.5 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-800">{title}</span>
          <span className="text-[11px] text-slate-500">
            {items.length} chỉ tiêu · Đạt {counts.pass} · Không đạt {counts.fail}
            {counts.pending > 0 ? ` · Chưa kiểm ${counts.pending}` : ""}
          </span>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {toolbar}
          {editable && items.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={markAllPass}
              className="h-8 border-emerald-300 bg-white text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
            >
              <CheckCheck className="mr-1.5 h-3.5 w-3.5" />
              Tất cả Đạt
            </Button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-slate-400">{emptyText || "Chưa có chỉ tiêu. Chọn mẫu kiểm tra."}</div>
      ) : compact ? (
        <div className="divide-y divide-slate-100">
          {groups.map((g) => (
            <React.Fragment key={g.key || "all"}>
              {g.key && <div className="bg-slate-50 px-3 py-1.5 text-[11px] font-bold uppercase text-slate-600">{g.key}</div>}
              {g.rows.map(({ item, idx }) => (
                <div
                  key={item.id}
                  data-testid={`qc-criterion-${item.id}`}
                  className={cn("space-y-2 px-3 py-3", item.result === "FAIL" && "bg-red-50/40")}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900">
                        {idx + 1}. {displayCriterionName(item, idx)}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {isQuantitativeCriterion(item) ? "Định lượng" : "Định tính"}
                        {criterionSubline(item) ? ` · ${criterionSubline(item)}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-700">
                    <span className="text-slate-500">Tiêu chuẩn: </span>
                    <span className={isQuantitativeCriterion(item) ? "font-mono" : ""}>{formatCriterionStandard(item)}</span>
                  </div>
                  {isQuantitativeCriterion(item) ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1">{renderResultCell(item, true)}</div>
                      {renderEvalCell(item, true)}
                    </div>
                  ) : (
                    <div>{renderEvalCell(item, true)}</div>
                  )}
                  {(editable || item.note) && <div>{noteInput(item, true)}</div>}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-slate-200 bg-slate-50/70 text-slate-600">
              <tr>
                <th className="w-8 px-2 py-2 text-center font-bold">#</th>
                <th className="px-2.5 py-2 text-left font-bold">Chỉ tiêu</th>
                <th className="w-24 px-2 py-2 text-center font-bold">Loại</th>
                <th className="w-44 px-2.5 py-2 text-left font-bold">Tiêu chuẩn</th>
                <th className="w-36 px-2 py-2 text-center font-bold">Kết quả</th>
                <th className="w-44 px-2 py-2 text-center font-bold">Đánh giá</th>
                <th className="w-48 px-2.5 py-2 text-left font-bold">Ghi chú</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {groups.map((g) => (
                <React.Fragment key={g.key || "all"}>
                  {g.key && (
                    <tr className="bg-slate-50">
                      <td colSpan={7} className="px-2.5 py-1.5 text-[11px] font-bold uppercase text-slate-600">
                        {g.key}
                      </td>
                    </tr>
                  )}
                  {g.rows.map(({ item, idx }) => {
                    const quant = isQuantitativeCriterion(item);
                    const std = formatCriterionStandard(item);
                    return (
                      <tr
                        key={item.id}
                        data-testid={`qc-criterion-${item.id}`}
                        className={item.result === "FAIL" ? "bg-red-50/40" : "hover:bg-slate-50/60"}
                      >
                        <td className="px-2 py-2 text-center font-mono text-slate-400">{idx + 1}</td>
                        <td className="px-2.5 py-2">
                          <div className="font-semibold text-slate-900" title={item.name}>
                            {displayCriterionName(item, idx)}
                          </div>
                          {criterionSubline(item) && <div className="text-[11px] text-slate-500">{criterionSubline(item)}</div>}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-center text-[11px] text-slate-600">{quant ? "Định lượng" : "Định tính"}</td>
                        <td className={cn("px-2.5 py-2 text-slate-700", quant && "font-mono")} title={item.standard || std}>
                          <span className="line-clamp-2">{std}</span>
                        </td>
                        {quant ? (
                          <>
                            <td className="px-2 py-2">{renderResultCell(item)}</td>
                            <td className="px-2 py-2 text-center">{renderEvalCell(item)}</td>
                          </>
                        ) : (
                          <td colSpan={2} className="px-2 py-2 text-center">
                            {renderEvalCell(item)}
                          </td>
                        )}
                        <td className="px-2.5 py-2">{noteInput(item)}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
